import { Injectable, OnDestroy } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute } from '@angular/router';
import { Connection as ApideckConnection, ApideckVault } from '@apideck/vault-js';
import {
  AccountingSetupFormState,
  AccountMapping,
  Customer,
  Owner,
  Property,
  Supplier,
} from '@app/modules/businesses/models/statements.models';
import {
  Connection,
  ConnectionAccountCategory,
  TrackingCategory,
} from '@app/modules/businesses/models/accounting.interface';

import { StatementsFacade } from '@app/modules/businesses/services/statements.facade';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'any',
})
export class StatementsAccountingIntegrationService implements OnDestroy {
  private destroy$ = new Subject<void>();

  public connectionId = Number(this.route.snapshot.params['id']);

  public connection$ = this.statementsFacade.connectionsState$.pipe(
    // If we haven't fetched the connections yet, fetch them
    tap((connectionsState) => {
      if (!connectionsState.data?.length && !connectionsState.loading && !connectionsState.error) {
        this.statementsFacade.listConnections();
      }
    }),

    filter((connectionsState) => Boolean(connectionsState.data?.length)),
    map((connectionsState) =>
      connectionsState.data?.find((connection) => connection.id === Number(this.route.snapshot.params['id']))
    )
  );

  public configuredProperties$ = this.statementsFacade.getPropertiesForSetup();
  public connecting$ = new BehaviorSubject<boolean>(false);
  public loadingVault$ = new BehaviorSubject<boolean>(false);
  public deletingConnection$ = new BehaviorSubject<
    Record<
      ApideckConnection['id'],
      {
        inProgress: boolean;
        error: boolean;
        success: boolean;
      }
    >
  >({});

  public formState$ = new BehaviorSubject<AccountingSetupFormState>({
    data: null,
    loading: false,
    error: null,
  });

  constructor(
    private statementsFacade: StatementsFacade,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private toast: ToastNotificationsService
  ) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openVault() {
    this.loadingVault$.next(true);
    this.statementsFacade.createAccountingConnection().subscribe((res) => {
      ApideckVault.open({
        token: res.token,
        serviceId: 'quickbooks',
        showAttribution: false,
        unifiedApi: 'accounting',
        onReady: () => {
          this.loadingVault$.next(false);
        },
        onConnectionChange: (connection: ApideckConnection) => {
          if (connection.state !== 'callable') {
            return;
          }

          this.connecting$.next(true);
          ApideckVault.close();
        },
      });
    });
  }

  public openVaultForConnection(connection: ApideckConnection) {
    this.loadingVault$.next(true);
    this.statementsFacade.createAccountingConnection().subscribe((res) => {
      ApideckVault.open({
        token: res.token,
        serviceId: connection.service_id,
        showAttribution: false,
        unifiedApi: 'accounting',
        onReady: () => {
          this.loadingVault$.next(false);
        },
      });
    });
  }

  public connected() {
    this.connecting$.next(false);
  }

  deleteConnectionPrompt(connection: Connection) {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: connection.name,
        action: 'Disconnect',
        additionalText:
          'Disconnecting this integration will stop pushing reservation financials and transactions to your accounting software.',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteConnection(connection.id);
      }
    });
  }

  private deleteConnection(connectionId: Connection['id']) {
    this.deletingConnection$.next({
      ...this.deletingConnection$.value,
      [connectionId]: {
        inProgress: true,
        error: false,
        success: false,
      },
    });
    this.statementsFacade
      .deleteAccountingConnection(connectionId)
      .pipe(
        catchError((error) => {
          this.deletingConnection$.next({
            ...this.deletingConnection$.value,
            [connectionId]: {
              inProgress: false,
              error: true,
              success: false,
            },
          });
          return of({ error: error.error[0] ?? error.message ?? 'There was an error deleting the connection' });
        })
      )
      .subscribe((resp) => {
        if ((resp as { error: any })?.error) {
          this.toast.open((resp as { error: any }).error, 'Dismiss', NotificationType.Error);
          return;
        }

        this.statementsFacade.listConnections();
        this.deletingConnection$.next({
          ...this.deletingConnection$.value,
          [connectionId]: {
            inProgress: false,
            error: false,
            success: true,
          },
        });
      });
  }

  /**
   * Initialize form data by fetching all required data in parallel
   * @param connectionId The ID of the connection being configured
   * @param isEditing Whether we're editing an existing configuration
   */
  public initializeFormData(connectionId: number): Observable<void> {
    this.formState$.next({ ...this.formState$.value, loading: true });

    this.statementsFacade.getOwners(['external_type']);

    // Create observables for each piece of data we need
    const accountMapping$ = this.statementsFacade.listLedgerAccounts(connectionId);
    const suppliers$ = this.statementsFacade.getSupplierAccounts(connectionId);
    const customers$ = this.statementsFacade.getCustomerAccounts(connectionId);
    const trackingCategories$ = this.statementsFacade.getTrackingCategories(connectionId);
    const properties$ = this.statementsFacade.getPropertiesForSetup();
    const owners$ = this.statementsFacade.ownersState$.pipe(
      filter((state) => !state.loading && state.data !== null),
      map((state) => state.data)
    );
    const currentConfig$ = this.statementsFacade.getCurrentAccountMapping(connectionId);

    // Combine all data streams and transform into form state
    return combineLatest([
      accountMapping$,
      suppliers$,
      customers$,
      trackingCategories$,
      properties$,
      owners$,
      currentConfig$,
    ]).pipe(
      take(1),
      map(([accountMapping, suppliers, customers, trackingCategories, properties, owners, currentConfig]) => {
        const formData = this.transformToNormalizedState(
          accountMapping,
          suppliers,
          customers,
          trackingCategories,
          properties,
          owners,
          currentConfig
        );

        this.formState$.next({
          data: formData,
          loading: false,
          error: null,
        });
      }),
      catchError((error) => {
        this.formState$.next({
          data: null,
          loading: false,
          error: error.error?.message || 'An error occurred loading form data',
        });
        return of(null);
      })
    );
  }

  private transformToNormalizedState(
    accountMapping: AccountMapping,
    suppliers: Supplier[],
    customers: Customer[],
    trackingCategories: TrackingCategory[],
    properties: Property[],
    owners: Owner[],
    currentConfig: any
  ) {
    return {
      accounts: {
        fieldMap: this.createAccountFieldMap(accountMapping, currentConfig),
        options: accountMapping.available_accounts as AccountingSetupFormState['data']['accounts']['options'],
      },
      properties: {
        fieldMap: this.createPropertyFieldMap(
          properties.filter((p) => p.commissionRates.length && p.owners.length),
          currentConfig
        ),
        options: trackingCategories,
      },
      owners: {
        fieldMap: this.createOwnerFieldMap(owners, currentConfig),
        options: {
          suppliers,
          customers,
        },
      },
    };
  }

  private createAccountFieldMap(accountMapping: AccountMapping, currentConfig: any) {
    if (!accountMapping?.accounts_to_map) {
      return {};
    }

    return accountMapping.accounts_to_map.reduce((fieldMap, account) => {
      const configuredAccount = currentConfig?.accounts?.[account.category]?.find(
        (cfg) => cfg.reference === account.account_reference
      );
      fieldMap[account.account_reference] = {
        id: account.account_reference,
        category: account.category as ConnectionAccountCategory,
        label: account.name,
        value: configuredAccount?.id || '',
        isDefault: configuredAccount ? configuredAccount.is_default : true,
      };

      return fieldMap;
    }, {});
  }

  private createPropertyFieldMap(properties: Property[], currentConfig: any) {
    return properties.reduce((fieldMap, property) => {
      const configuredProperty = currentConfig?.properties?.find((cfg) => cfg.id === property.id);
      fieldMap[property.id] = {
        id: property.id,
        label: property.name,
        value: configuredProperty?.tracking_category?.id || '',
        isDefault: configuredProperty ? configuredProperty.tracking_category.is_default : true,
      };

      return fieldMap;
    }, {});
  }

  private createOwnerFieldMap(owners: Owner[], currentConfig: any) {
    return owners.reduce((acc, owner) => {
      const configuredOwner = currentConfig?.owners?.find((cfg) => cfg.id === owner.id && Boolean(cfg.external_id));
      acc[owner.id] = {
        id: owner.id,
        label: `${owner.firstName} ${owner.name}`,
        value: configuredOwner?.external_id ?? '',
        isDefault: configuredOwner ? configuredOwner.external_id : true,
        propertyIds: owner.properties.map((property) => property.id),
        externalType: owner.externalType,
      };

      return acc;
    }, {});
  }
}
